import InstallmentsService from "@/services/InstallmentsService";
import { getListItems, replaceItemInList } from "@/utils/listHelpers";
import { notifyStoreError } from "@/utils/storeHelpers";
import { isFinancialManagerType, resolveInstallmentBucket } from "@/utils/installmentHelpers";
const errorMessages = {
    list: 'Erro ao buscar as parcelas. Tente novamente.',
    stats: 'Erro ao buscar o resumo financeiro. Tente novamente.',
    pay: 'Erro ao gerar a cobrança da parcela. Tente novamente.',
    settle: 'Erro ao dar baixa nas parcelas. Tente novamente.',
    alreadySettled: 'Uma ou mais parcelas já foram pagas ou baixadas. Atualize a página e tente novamente.',
};
const extractPaymentErrorMessage = (error) => {
    const responseData = error?.response?.data;
    return responseData?.message || responseData?.errors?.payment_method_id?.[0] || null;
};
const extractSettleErrorMessage = (error) => {
    const response = error?.response;
    if (response?.status !== 422)
        return errorMessages.settle;
    const errors = response.data?.errors;
    if (!errors)
        return errorMessages.settle;
    const hasPendencyError = Object.keys(errors).some((key) => key.startsWith('financial_pendency_ids'));
    if (hasPendencyError)
        return errorMessages.alreadySettled;
    return Object.values(errors)[0]?.[0] || errorMessages.settle;
};
const resolveStudentId = (getters, payloadStudentId) => {
    return payloadStudentId ?? getters.getEffectiveStudentId ?? null;
};
const isStillTheSelectedStudent = (getters, payloadStudentId, requestedStudentId) => {
    if (payloadStudentId)
        return true;
    return getters.getEffectiveStudentId === requestedStudentId;
};
const toMoney = (value) => Math.round(value * 100) / 100;
const sumAmount = (installments) => installments.reduce((total, installment) => total + Number(installment.amount), 0);
const earliestByDueDate = (installments) => {
    return installments
        .filter((installment) => installment.due_date)
        .sort((first, second) => first.due_date.localeCompare(second.due_date))[0] ?? null;
};
const inBucket = (installments, bucket) => installments.filter((installment) => resolveInstallmentBucket(installment) === bucket);
const patchStatsAfterSettlement = (stats, settled, remaining) => {
    const settledPending = inBucket(settled, 'pending');
    const settledOverdue = inBucket(settled, 'overdue');
    const settledPendingAmount = sumAmount(settledPending);
    const settledOverdueAmount = sumAmount(settledOverdue);
    const nextPending = earliestByDueDate(inBucket(remaining, 'pending'));
    const oldestOverdue = earliestByDueDate(inBucket(remaining, 'overdue'));
    return {
        ...stats,
        pending: {
            ...stats.pending,
            count: stats.pending.count - settledPending.length,
            total_amount: toMoney(stats.pending.total_amount - settledPendingAmount),
            next_due_date: nextPending?.due_date ?? null,
            next_due_amount: nextPending ? toMoney(Number(nextPending.amount)) : 0
        },
        overdue: {
            ...stats.overdue,
            count: stats.overdue.count - settledOverdue.length,
            total_base_amount: toMoney(stats.overdue.total_base_amount - settledOverdueAmount),
            total_amount_with_interest: toMoney(stats.overdue.total_amount_with_interest - settledOverdueAmount),
            oldest_due_date: oldestOverdue?.due_date ?? null
        },
        summary: {
            ...stats.summary,
            open_installments: stats.summary.open_installments - settledPending.length - settledOverdue.length,
            open_total_amount: toMoney(stats.summary.open_total_amount - settledPendingAmount - settledOverdueAmount)
        }
    };
};
let installmentsRequestSequence = 0;
const releaseSkeleton = (dispatch, commit, skeletonKey) => {
    commit('clearInstallments');
    if (skeletonKey) {
        dispatch('skeleton/setLoadingState', { key: skeletonKey, value: false }, { root: true });
    }
    return null;
};
export default {
    namespaced: true,
    state: {
        installmentsData: [],
        installmentsCurrentPage: 1,
        installmentsLastPage: 1,
        installmentsTotal: 0,
        selectedInstallments: {},
        statsData: null,
        status: null,
        selectedStudentId: null,
        selectedStudentName: null,
    },
    getters: {
        getInstallmentsData: (state) => state.installmentsData,
        getInstallmentsCurrentPage: (state) => state.installmentsCurrentPage,
        getInstallmentsLastPage: (state) => state.installmentsLastPage,
        getSelectedInstallments: (state) => Object.values(state.selectedInstallments),
        getSelectedInstallmentsCount: (state) => Object.keys(state.selectedInstallments).length,
        getStatsData: (state) => state.statsData,
        getStatus: (state) => state.status,
        getSelectedStudentId: (state) => state.selectedStudentId,
        getSelectedStudentName: (state) => state.selectedStudentName,
        getEffectiveStudentId: (state, getters, rootState, rootGetters) => {
            if (isFinancialManagerType(rootGetters?.getType))
                return state.selectedStudentId;
            return rootGetters?.getId ?? null;
        },
    },
    mutations: {
        setInstallmentsData(state, payload) {
            state.installmentsData = Array.isArray(payload?.data) ? payload.data : [];
            state.installmentsCurrentPage = payload?.meta?.current_page ?? 1;
            state.installmentsLastPage = payload?.meta?.last_page ?? 1;
            state.installmentsTotal = payload?.meta?.total ?? state.installmentsData.length;
        },
        updateInstallmentInList(state, installment) {
            state.installmentsData = getListItems(replaceItemInList(state.installmentsData, installment));
        },
        toggleInstallmentSelection(state, installment) {
            const nextSelection = { ...state.selectedInstallments };
            if (nextSelection[installment.id])
                delete nextSelection[installment.id];
            else
                nextSelection[installment.id] = installment;
            state.selectedInstallments = nextSelection;
        },
        selectInstallments(state, installments) {
            const nextSelection = { ...state.selectedInstallments };
            installments.forEach((installment) => {
                nextSelection[installment.id] = installment;
            });
            state.selectedInstallments = nextSelection;
        },
        deselectInstallments(state, installmentIds) {
            const nextSelection = { ...state.selectedInstallments };
            installmentIds.forEach((installmentId) => {
                delete nextSelection[installmentId];
            });
            state.selectedInstallments = nextSelection;
        },
        clearInstallmentSelection(state) {
            state.selectedInstallments = {};
        },
        setStatsData(state, data) {
            state.statsData = data;
        },
        setStatus(state, status) {
            state.status = status;
        },
        setSelectedStudent(state, { studentId, studentName }) {
            state.selectedStudentId = studentId;
            state.selectedStudentName = studentId ? (studentName ?? null) : null;
        },
        clearInstallments(state) {
            state.installmentsData = [];
            state.installmentsCurrentPage = 1;
            state.installmentsLastPage = 1;
            state.installmentsTotal = 0;
            state.selectedInstallments = {};
            state.statsData = null;
            state.status = null;
        },
        applySettlement(state, installmentIds) {
            const idsToSettle = new Set(installmentIds);
            const settled = state.installmentsData.filter((installment) => idsToSettle.has(installment.id));
            const nextSelection = { ...state.selectedInstallments };
            installmentIds.forEach((installmentId) => {
                delete nextSelection[installmentId];
            });
            state.selectedInstallments = nextSelection;
            if (!settled.length)
                return;
            state.installmentsData = state.installmentsData.map((installment) => idsToSettle.has(installment.id) ? { ...installment, status: 'settled' } : installment);
            if (!state.statsData)
                return;
            state.statsData = patchStatsAfterSettlement(state.statsData, settled, state.installmentsData);
        },
    },
    actions: {
        selectStudent({ commit, state }, payload) {
            const { studentId, studentName } = typeof payload === 'object' && payload !== null
                ? payload
                : { studentId: payload, studentName: null };
            const isSameStudent = studentId === state.selectedStudentId;
            commit('setSelectedStudent', {
                studentId,
                studentName: studentName ?? (isSameStudent ? state.selectedStudentName : null)
            });
            commit('clearInstallments');
        },
        async getInstallments({ commit, getters, dispatch }, payload) {
            const studentId = resolveStudentId(getters, payload.studentId);
            if (!studentId)
                return releaseSkeleton(dispatch, commit, payload.skeletonKey);
            const requestId = ++installmentsRequestSequence;
            try {
                const response = await InstallmentsService.getInstallments(studentId, payload.userToken, {
                    page: payload.page || 1,
                    status: payload.status || ''
                });
                if (requestId === installmentsRequestSequence && isStillTheSelectedStudent(getters, payload.studentId, studentId)) {
                    commit('setInstallmentsData', response);
                }
            }
            catch (error) {
                commit('setStatus', errorMessages.list);
                notifyStoreError(dispatch, errorMessages.list, error);
            }
            finally {
                if (payload.skeletonKey) {
                    dispatch('skeleton/setLoadingState', { key: payload.skeletonKey, value: false }, { root: true });
                }
            }
        },
        async getInstallmentsStats({ commit, getters, dispatch }, payload) {
            const studentId = resolveStudentId(getters, payload.studentId);
            if (!studentId)
                return releaseSkeleton(dispatch, commit, payload.skeletonKey);
            try {
                const response = await InstallmentsService.getInstallmentsStats(studentId, payload.userToken);
                if (isStillTheSelectedStudent(getters, payload.studentId, studentId)) {
                    commit('setStatsData', response);
                }
            }
            catch (error) {
                commit('setStatus', errorMessages.stats);
                notifyStoreError(dispatch, errorMessages.stats, error);
            }
            finally {
                if (payload.skeletonKey) {
                    dispatch('skeleton/setLoadingState', { key: payload.skeletonKey, value: false }, { root: true });
                }
            }
        },
        async payInstallment({ commit, getters, dispatch }, payload) {
            const studentId = resolveStudentId(getters, payload.data.studentId);
            if (!studentId)
                return null;
            try {
                const response = await InstallmentsService.payInstallment(studentId, payload.data.installmentId, payload.data.paymentMethodId, payload.userToken);
                commit('updateInstallmentInList', response.data);
                dispatch('toastfy/activeSuccesNotification', { text: 'Cobrança gerada com sucesso' }, { root: true });
                return response.data;
            }
            catch (error) {
                const backendMessage = extractPaymentErrorMessage(error);
                if (backendMessage && backendMessage.includes('CPF'))
                    throw error;
                return notifyStoreError(dispatch, backendMessage || errorMessages.pay, error);
            }
        },
        async settleInstallments({ commit, getters, dispatch }, payload) {
            const studentId = resolveStudentId(getters);
            if (!studentId || !payload.data.installmentIds.length)
                return null;
            try {
                await InstallmentsService.settleInstallments(studentId, payload.data.installmentIds, payload.data.reason, payload.userToken);
                commit('applySettlement', payload.data.installmentIds);
                dispatch('toastfy/activeSuccesNotification', { text: 'Baixa efetuada com sucesso' }, { root: true });
                dispatch('getInstallmentsStats', { userToken: payload.userToken });
                return true;
            }
            catch (error) {
                return notifyStoreError(dispatch, extractSettleErrorMessage(error), error);
            }
        },
    }
};
